import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ErrorApi } from '../api/cliente';
import type { Sesion } from '../api/tipos';
import { configuracionCompleta, iniciarMsal, modoDev } from './msal';

export type EstadoAuth = 'iniciando' | 'sin-config' | 'anonimo' | 'autenticado';

interface Contexto {
  estado: EstadoAuth;
  sesion: Sesion | null;
  errorSesion: ErrorApi | null;
  cargandoSesion: boolean;
}

const ContextoSesion = createContext<Contexto | null>(null);

export function ProveedorSesion({ children }: { children: ReactNode }) {
  const [estadoMsal, setEstadoMsal] = useState<EstadoAuth>(() => {
    if (modoDev) return 'autenticado';
    return configuracionCompleta ? 'iniciando' : 'sin-config';
  });

  useEffect(() => {
    if (modoDev || !configuracionCompleta) return;
    let vivo = true;

    iniciarMsal()
      .then((cuenta) => {
        if (vivo) setEstadoMsal(cuenta ? 'autenticado' : 'anonimo');
      })
      .catch((error: unknown) => {
        console.error('No se pudo inicializar MSAL', error);
        if (vivo) setEstadoMsal('anonimo');
      });

    return () => {
      vivo = false;
    };
  }, []);

  const consulta = useQuery({
    queryKey: ['sesion'],
    queryFn: () => api.get<Sesion>('/sesion'),
    enabled: estadoMsal === 'autenticado',
    retry: (intentos, error) => !(error instanceof ErrorApi) && intentos < 2,
    staleTime: 5 * 60 * 1000,
  });

  const valor: Contexto = {
    estado: estadoMsal,
    sesion: consulta.data ?? null,
    errorSesion: consulta.error instanceof ErrorApi ? consulta.error : null,
    cargandoSesion: consulta.isPending,
  };

  return <ContextoSesion.Provider value={valor}>{children}</ContextoSesion.Provider>;
}

export function useSesion(): Contexto {
  const contexto = useContext(ContextoSesion);
  if (!contexto) throw new Error('useSesion tiene que usarse dentro de <ProveedorSesion>.');
  return contexto;
}

/// Sesión ya resuelta. Solo para componentes que se montan detrás del guard.
export function useSesionActiva(): Sesion {
  const { sesion } = useSesion();
  if (!sesion) throw new Error('No hay sesión cargada en este punto del árbol.');
  return sesion;
}
